import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Hourglass } from 'lucide-react';
import { Button, buttonVariants, Input, Modal, Select, toast } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { formatHours } from '../../lib/format';
import { useAgreements } from '../agreements/api';
import type { InvoiceLineItem } from './api';

/**
 * PREPAID HOURS ON AN INVOICE LINE — banked, or the repair to bank them.
 *
 * Jesse, 2026-09-30: invoices built by hand for prepaid hours created no hour
 * block, so the hours never reached the agreement's bank — *"how do I repair
 * them?"*. A line the server says sells hours (`prepaid.canAdd`) offers
 * "Add N h to the bank": one dialog that names the agreement (linking the line
 * to it if it is not already) and puts the hours in that agreement's bank,
 * usable once the invoice is paid. A line whose hours are already banked says
 * so instead. Everything else renders nothing.
 */
export function LineBankAction({
  invoiceId,
  invoiceNumber,
  invoiceSent,
  clientId,
  line,
  asButton = false,
}: {
  invoiceId: string;
  invoiceNumber: string;
  /** Hours on a SENT invoice count in the bank straight away; a draft's once it goes. */
  invoiceSent: boolean;
  clientId: string;
  /** The line, as much of it as the repair needs — the support bank passes an unbanked sale. */
  line: Pick<InvoiceLineItem, 'id' | 'agreement' | 'quantity' | 'description' | 'prepaid'>;
  /** Render the trigger as a small button (the support bank) instead of a caption link. */
  asButton?: boolean;
}) {
  const whenTheyCount = invoiceSent ? 'they count straight away' : `they count once #${invoiceNumber} is sent`;
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState(line.agreement?.id ?? '');
  const [expiresAt, setExpiresAt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const agreements = useAgreements({ clientId, showArchived: true });

  const bank = useMutation({
    mutationFn: async () => {
      // The hours belong to an agreement: link the line first when it names
      // none (or another), through the one line-link writer, then bank it.
      if (choice !== (line.agreement?.id ?? '')) {
        await api.patch(`/invoices/${invoiceId}/lines/${line.id}/agreement`, { agreementId: choice });
      }
      return api.post<{ hours: number; agreement: { name: string } }>('/support/banks/blocks/from-invoice-line', {
        invoiceId,
        lineId: line.id,
        expiresAt: expiresAt || null,
      });
    },
    onSettled: () => {
      for (const key of ['invoices', 'agreements', 'support', 'clients']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
    },
  });

  const prepaid = line.prepaid;
  if (!prepaid) return null;
  if (prepaid.bankedHours !== null) {
    return (
      <span className="inline-flex items-center gap-1 normal-case">
        <Hourglass className="h-3 w-3" aria-hidden />
        {formatHours(prepaid.bankedHours, { unit: true })} in the bank
      </span>
    );
  }
  if (!prepaid.canAdd) return null;

  const rows = [...(agreements.data?.agreements ?? [])].sort(
    (a, b) => Number(b.isActive) - Number(a.isActive) || a.name.localeCompare(b.name),
  );
  const hours = formatHours(line.quantity, { unit: true });

  const submit = async () => {
    setError(null);
    if (!choice) {
      setError('Choose the agreement these hours are for.');
      return;
    }
    try {
      const result = await bank.mutateAsync();
      toast.success(
        `${formatHours(result.hours, { unit: true })} added to ${result.agreement.name}'s bank — ${whenTheyCount}.`,
      );
      setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The hours could not be added.');
    }
  };

  return (
    <>
      <button
        type="button"
        className={
          asButton
            ? buttonVariants({ size: 'sm' })
            : 'inline-flex items-center gap-1 font-semibold normal-case text-primary hover:underline'
        }
        onClick={() => {
          setChoice(line.agreement?.id ?? '');
          setExpiresAt('');
          setError(null);
          setOpen(true);
        }}
        title="These hours were sold on this invoice but never reached a bank"
      >
        <Hourglass className="h-3 w-3" aria-hidden />
        Add {hours} to the bank
      </button>
      <Modal
        open={open}
        onOpenChange={(next) => !bank.isPending && setOpen(next)}
        title={`Add ${hours} to the bank`}
        description={`This line sold prepaid hours but none reached a bank. They go on the agreement below, and ${whenTheyCount}. The invoice itself does not change.`}
        footer={
          <>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={bank.isPending}>
              Cancel
            </Button>
            <Button onClick={submit} loading={bank.isPending} disabled={!choice}>
              Add {hours}
            </Button>
          </>
        }
      >
        <p className="mb-3 text-body-sm text-secondary">{line.description}</p>
        <div className="space-y-4">
          <Select
            label="Agreement"
            placeholder={agreements.isLoading ? 'Loading agreements…' : 'Choose the agreement…'}
            options={rows.map((a) => ({ value: a.id, label: a.isActive ? a.name : `${a.name} (archived)` }))}
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            disabled={agreements.isLoading}
            error={error ?? undefined}
          />
          <Input
            label="Hours expire on"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            hint="Optional — blank never expires."
          />
        </div>
      </Modal>
    </>
  );
}
